import { z } from 'zod'

/**
 * Per-organization pricing overrides (docs/PLAN-pricing-overrides.md).
 *
 * A global catalog of alternative Stripe prices ("price options") per plan x
 * billing interval, assigned to organizations from the admin app. The billed
 * price for every Stripe write (checkout, trial activation, plan change) comes
 * from one resolver: `resolveEffectivePrice`. Amounts are integer cents per
 * seat per billing interval.
 */

export type PricingInterval = 'month' | 'year'
export const PRICING_INTERVALS: readonly PricingInterval[] = ['month', 'year']

export enum PriceOptionStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  RETIRED = 'RETIRED',
  ARCHIVED = 'ARCHIVED',
}

export enum PriceOptionKind {
  CUSTOM = 'CUSTOM',
  LEGACY = 'LEGACY',
}

export enum PriceOverrideStatus {
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  ENDING = 'ENDING',
  ENDED = 'ENDED',
  CANCELED = 'CANCELED',
}

export enum PriceOverrideSyncState {
  NOT_REQUIRED = 'NOT_REQUIRED',
  PENDING = 'PENDING',
  SYNCED = 'SYNCED',
  FAILED = 'FAILED',
  BLOCKED = 'BLOCKED',
}

export enum PriceOverrideApplyMode {
  NEXT_PERIOD = 'NEXT_PERIOD',
  IMMEDIATE = 'IMMEDIATE',
}

/** Override statuses that occupy a plan x interval cell. */
export const LIVE_PRICE_OVERRIDE_STATUSES: readonly PriceOverrideStatus[] = [
  PriceOverrideStatus.SCHEDULED,
  PriceOverrideStatus.ACTIVE,
]

/** Override statuses that still matter for billing or display. */
export const OPEN_PRICE_OVERRIDE_STATUSES: readonly PriceOverrideStatus[] = [
  PriceOverrideStatus.SCHEDULED,
  PriceOverrideStatus.ACTIVE,
  PriceOverrideStatus.ENDING,
]

/** Bootstrap presets: percentage off the standard price per tier x interval. */
export const PRICE_OPTION_PRESETS: readonly {
  label: string
  percentOff: number
}[] = [
  { label: 'Early partner', percentOff: 20 },
  { label: 'Founding customer', percentOff: 30 },
  { label: 'Nonprofit / Education', percentOff: 50 },
]

/** Plans that take price options (tiers sold self-serve). */
export const PRICE_OPTION_PLAN_KEYS: readonly string[] = [
  'core',
  'pro',
  'business',
]

// ---------------------------------------------------------------------------
// Pure resolver
// ---------------------------------------------------------------------------

export interface PricingPlanInput {
  id: string
  key: string
  currency?: string | null
  stripePriceId?: string | null
  stripeAnnualPriceId?: string | null
  /** Monthly price per seat in dollars (catalog). */
  price: number
  /** Annual price per seat in dollars (catalog); defaults to 12x monthly. */
  annualPrice?: number | null
  minSeats?: number | null
}

export interface PricingOverrideInput {
  id: string
  status: PriceOverrideStatus | `${PriceOverrideStatus}`
  minSeatsOverride?: number | null
  startsAt?: Date | string | null
  endsAt?: Date | string | null
  priceOption: {
    id: string
    label: string
    unitAmount: number
    currency?: string | null
    stripePriceId?: string | null
    stripeActive?: boolean | null
    status: PriceOptionStatus | `${PriceOptionStatus}`
  }
}

export type ResolvedPriceSource = 'standard' | 'override'

export interface ResolvedPrice {
  planId: string
  planKey: string
  interval: PricingInterval
  /** The Stripe price to bill; null when the plan has none configured. */
  stripePriceId: string | null
  /** Cents per seat per interval that will be billed. */
  unitAmount: number
  standardStripePriceId: string | null
  standardUnitAmount: number
  currency: string
  source: ResolvedPriceSource
  overrideId?: string
  priceOptionId?: string
  label?: string
  minSeats: number
  /** Overrides are final prices: no promotion codes on top. */
  allowPromotionCodes: boolean
  /** Set when an override exists but cannot be billed (fell back to standard). */
  unavailableOverrideId?: string
}

export function toCents(dollars: number | null | undefined): number {
  const value = Number(dollars ?? 0)
  return Number.isFinite(value) ? Math.round(value * 100) : 0
}

export function standardUnitAmountFor(
  plan: Pick<PricingPlanInput, 'price' | 'annualPrice'>,
  interval: PricingInterval,
): number {
  if (interval === 'year') {
    const annual =
      plan.annualPrice != null && Number(plan.annualPrice) > 0
        ? Number(plan.annualPrice)
        : Number(plan.price) * 12
    return toCents(annual)
  }
  return toCents(plan.price)
}

export function standardStripePriceIdFor(
  plan: Pick<PricingPlanInput, 'stripePriceId' | 'stripeAnnualPriceId'>,
  interval: PricingInterval,
): string | null {
  return (
    (interval === 'year' ? plan.stripeAnnualPriceId : plan.stripePriceId) ??
    null
  )
}

export function monthlyEquivalent(
  unitAmount: number,
  interval: PricingInterval,
): number {
  return interval === 'year' ? Math.round(unitAmount / 12) : unitAmount
}

/** A price option an override can bill on right now. */
export function isBillablePriceOption(
  option: PricingOverrideInput['priceOption'],
): boolean {
  return Boolean(
    option.stripePriceId &&
    option.stripeActive !== false &&
    (option.status === PriceOptionStatus.ACTIVE ||
      option.status === PriceOptionStatus.RETIRED),
  )
}

/**
 * The price every new Stripe write for (plan, interval) must use.
 *
 * A SCHEDULED or ACTIVE override applies (a SCHEDULED one only exists on the
 * live subscription's current cell and is the price the next period bills);
 * an ENDING override has been removed, so standard pricing applies. An
 * override whose option cannot be billed falls back to standard and reports
 * `unavailableOverrideId` so callers can alert.
 */
export function resolveEffectivePrice(input: {
  plan: PricingPlanInput
  interval: PricingInterval
  overrides?: readonly PricingOverrideInput[] | null
}): ResolvedPrice {
  const { plan, interval } = input
  const standardUnitAmount = standardUnitAmountFor(plan, interval)
  const standardStripePriceId = standardStripePriceIdFor(plan, interval)
  const currency = (plan.currency || 'usd').toLowerCase()
  const planMinSeats = Math.max(1, plan.minSeats ?? 1)

  const standard: ResolvedPrice = {
    planId: plan.id,
    planKey: plan.key,
    interval,
    stripePriceId: standardStripePriceId,
    unitAmount: standardUnitAmount,
    standardStripePriceId,
    standardUnitAmount,
    currency,
    source: 'standard',
    minSeats: planMinSeats,
    allowPromotionCodes: true,
  }

  const candidates = (input.overrides ?? []).filter(
    override =>
      override.status === PriceOverrideStatus.ACTIVE ||
      override.status === PriceOverrideStatus.SCHEDULED,
  )
  const override =
    candidates.find(o => o.status === PriceOverrideStatus.ACTIVE) ??
    candidates[0]
  if (!override) return standard

  if (!isBillablePriceOption(override.priceOption)) {
    return { ...standard, unavailableOverrideId: override.id }
  }

  return {
    ...standard,
    stripePriceId: override.priceOption.stripePriceId ?? null,
    unitAmount: override.priceOption.unitAmount,
    currency: (override.priceOption.currency || currency).toLowerCase(),
    source: 'override',
    overrideId: override.id,
    priceOptionId: override.priceOption.id,
    label: override.priceOption.label,
    minSeats: Math.max(1, override.minSeatsOverride ?? planMinSeats),
    allowPromotionCodes: false,
  }
}

/** `scrambl_{plan}_{interval}_{amount}_{slug}` — stable and idempotent. */
export function priceOptionLookupKey(input: {
  planKey: string
  interval: PricingInterval
  unitAmount: number
  label: string
}): string {
  const slug =
    input.label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'custom'
  return `scrambl_${input.planKey}_${input.interval}_${input.unitAmount}_${slug}`
}

/** Preset amount: percent off standard, rounded to the cent. */
export function presetUnitAmount(
  standardUnitAmount: number,
  percentOff: number,
): number {
  return Math.max(
    1,
    Math.round((standardUnitAmount * (100 - percentOff)) / 100),
  )
}

// ---------------------------------------------------------------------------
// Customer DTOs — GET /subscriptions/organizations/:id/pricing
// ---------------------------------------------------------------------------

export type PriceViewSource = 'standard' | 'override' | 'legacy'

export interface PriceView {
  /** Cents per seat per interval at catalog price. */
  standardUnitAmount: number
  /** Cents per seat per interval this organization pays (or would pay). */
  effectiveUnitAmount: number
  /** Cents per seat per month for `effectiveUnitAmount`. */
  monthlyEquivalent: number
  standardMonthlyEquivalent: number
  currency: string
  source: PriceViewSource
  /** Customer-visible option label, e.g. "Founding customer". */
  overrideLabel?: string
  status?: 'SCHEDULED' | 'ACTIVE' | 'ENDING'
  effectiveFrom?: string
  endsAt?: string
  minSeats: number
  /** A price change already scheduled for the next billing period. */
  pendingChange?: {
    kind: 'start' | 'end'
    unitAmount: number
    monthlyEquivalent: number
    effectiveFrom: string
    label?: string
  }
}

export interface OrganizationPricingPlan {
  planId: string
  planKey: string
  displayName: string
  prices: { month: PriceView | null; year: PriceView | null }
}

export interface PricingNotice {
  kind: 'override_starts' | 'override_ends'
  planId: string
  interval: PricingInterval
  label?: string
  /** Cents per seat per interval from `effectiveFrom`. */
  unitAmount: number
  monthlyEquivalent: number
  effectiveFrom: string
}

export interface OrganizationPricing {
  currentPlanId: string | null
  currentInterval: PricingInterval | null
  notices: PricingNotice[]
  plans: OrganizationPricingPlan[]
}

// ---------------------------------------------------------------------------
// Admin DTOs — /admin/pricing
// ---------------------------------------------------------------------------

export interface PriceOptionView {
  id: string
  planId: string
  planKey: string
  planName: string
  interval: PricingInterval
  unitAmount: number
  monthlyEquivalent: number
  standardUnitAmount: number
  /** Positive = discount, negative = surcharge, relative to standard. */
  percentOffStandard: number
  currency: string
  label: string
  internalNote: string | null
  kind: PriceOptionKind
  status: PriceOptionStatus
  stripePriceId: string | null
  stripeLookupKey: string
  stripeActive: boolean
  stripePriceUrl: string | null
  lastStripeError: string | null
  /** Organizations with a scheduled, active or ending override on it. */
  organizationCount: number
  createdAt: string
  updatedAt: string
}

export interface PricingPlanSummary {
  id: string
  key: string
  displayName: string
  currency: string
  hasStripeProduct: boolean
  standard: Record<
    PricingInterval,
    { unitAmount: number; stripePriceId: string | null }
  >
}

export interface PriceOverrideView {
  id: string
  organizationId: string
  planId: string
  planKey: string
  interval: PricingInterval
  priceOptionId: string
  label: string
  unitAmount: number
  standardUnitAmount: number
  status: PriceOverrideStatus
  syncState: PriceOverrideSyncState
  syncError: string | null
  applyMode: PriceOverrideApplyMode
  endApplyMode: PriceOverrideApplyMode | null
  reason: string | null
  endReason: string | null
  minSeatsOverride: number | null
  startsAt: string | null
  activatedAt: string | null
  endsAt: string | null
  endedAt: string | null
  stripeScheduleId: string | null
  createdAt: string
}

export interface PricingAdminEventView {
  id: string
  type: string
  actorId: string | null
  actorName: string | null
  overrideId: string | null
  priceOptionId: string | null
  payload: Record<string, unknown>
  createdAt: string
}

export interface AdminPricingCell {
  planId: string
  planKey: string
  planName: string
  interval: PricingInterval
  standardUnitAmount: number
  standardStripePriceId: string | null
  /** Open overrides for this cell (ACTIVE/SCHEDULED plus any ENDING). */
  overrides: PriceOverrideView[]
  /** ACTIVE options assignable to this cell. */
  options: PriceOptionView[]
  isCurrent: boolean
}

export interface AdminOrganizationPricing {
  organizationId: string
  isPersonal: boolean
  subscription: {
    planId: string
    planKey: string
    interval: PricingInterval
    status: string
    stripeSubscriptionId: string | null
    stripePriceId: string | null
    stripePriceUrl: string | null
    unitAmount: number | null
    currentPeriodEnd: string | null
    cancelAt: string | null
    hasLiveStripeSubscription: boolean
  } | null
  cells: AdminPricingCell[]
  history: PricingAdminEventView[]
}

export interface PriceOverridePreview {
  effectiveFrom: string
  applyMode: PriceOverrideApplyMode
  currentUnitAmount: number | null
  newUnitAmount: number
  seats: number
  /** Net proration (cents) when applying immediately; 0 otherwise. */
  prorationAmount: number
  /** The next invoice total (cents) Stripe previews, when available. */
  nextInvoiceAmount: number | null
  currency: string
  notes: string[]
}

export interface BootstrapPriceOptionsResult {
  dryRun: boolean
  items: {
    planKey: string
    interval: PricingInterval
    label: string
    unitAmount: number
    standardUnitAmount: number
    action: 'create' | 'exists' | 'skipped'
    reason?: string
    priceOptionId?: string
  }[]
}

const intervalSchema = z.enum(['month', 'year'])
const applyModeSchema = z.nativeEnum(PriceOverrideApplyMode)

export const CreatePriceOptionSchema = z.object({
  planId: z.string().uuid(),
  interval: intervalSchema,
  unitAmount: z.number().int().positive().max(100_000_000),
  currency: z
    .string()
    .regex(/^[a-zA-Z]{3}$/)
    .optional(),
  label: z.string().trim().min(1).max(100),
  internalNote: z.string().trim().max(2000).optional(),
})
export type CreatePriceOptionInput = z.infer<typeof CreatePriceOptionSchema>

export const AdoptLegacyPriceSchema = z.object({
  stripePriceId: z.string().regex(/^price_[A-Za-z0-9]+$/),
  label: z.string().trim().min(1).max(100).optional(),
})
export type AdoptLegacyPriceInput = z.infer<typeof AdoptLegacyPriceSchema>

export const AssignPriceOverrideSchema = z.object({
  planId: z.string().uuid(),
  interval: intervalSchema,
  priceOptionId: z.string().uuid(),
  reason: z.string().trim().max(2000).optional(),
  minSeatsOverride: z.number().int().positive().max(100_000).optional(),
  applyMode: applyModeSchema.default(PriceOverrideApplyMode.NEXT_PERIOD),
})
export type AssignPriceOverrideInput = z.infer<typeof AssignPriceOverrideSchema>

export const RemovePriceOverrideSchema = z.object({
  reason: z.string().trim().max(2000).optional(),
  applyMode: applyModeSchema.default(PriceOverrideApplyMode.NEXT_PERIOD),
})
export type RemovePriceOverrideInput = z.infer<typeof RemovePriceOverrideSchema>

export const PreviewPriceOverrideSchema = z.object({
  planId: z.string().uuid(),
  interval: intervalSchema,
  priceOptionId: z.string().uuid().nullable(),
  applyMode: applyModeSchema.default(PriceOverrideApplyMode.NEXT_PERIOD),
})
export type PreviewPriceOverrideInput = z.infer<
  typeof PreviewPriceOverrideSchema
>

export const BootstrapPriceOptionsSchema = z.object({
  dryRun: z.boolean().default(true),
})
